import { FileDownload as FileDownloadIcon } from '@mui/icons-material';
import { Box, Button, CircularProgress } from '@mui/material';
import { Worker } from '@react-pdf-viewer/core';
import '@react-pdf-viewer/core/lib/styles/index.css';
import { rotatePlugin } from '@react-pdf-viewer/rotate';
import '@react-pdf-viewer/zoom/lib/styles/index.css';
import { zoomPlugin } from '@react-pdf-viewer/zoom';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { useInvoiceController } from 'api/controllers/InvoiceController';

import { Invoice } from 'openapi';

import { PdfViewerWithWatermark } from 'components/PdfViewerWithWatermark/PdfViewerWithWatermark';

import { PDF_JS_WORKER_URL } from 'utils/constants/constants';

import { zoomButtonsWrapper } from 'styles/pages/InvoiceVerificationStyle';

interface InvoiceFilePreviewProps {
  currentInvoice: Invoice;
}

export const InvoiceFilePreview = ({
  currentInvoice
}: InvoiceFilePreviewProps) => {
  const { getInvoiceFile } = useInvoiceController();
  const [pdfFileUrl, setPdfFileUrl] = useState<string>('');

  const isPdf = useMemo(
    () => Boolean(currentInvoice.filePath?.endsWith('.pdf')),
    [currentInvoice.filePath]
  );

  const zoomPluginInstance = zoomPlugin();
  const { ZoomInButton, ZoomOutButton, ZoomPopover } = zoomPluginInstance;

  const rotatePluginInstance = rotatePlugin();
  const { RotateBackwardButton, RotateForwardButton } = rotatePluginInstance;

  useEffect(() => {
    let isActive = true;
    getInvoiceFile(Number(currentInvoice.id)).then((fileData) => {
      if (isActive) {
        setPdfFileUrl(fileData.url);
      }
    });
    return () => {
      isActive = false;
    };
  }, [currentInvoice.id, getInvoiceFile]);

  const handleDownloadInvoice = useCallback(async () => {
    if (!pdfFileUrl) return;
    const link = document.createElement('a');
    link.href = pdfFileUrl;
    link.download = currentInvoice.filePath || 'document';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, [pdfFileUrl, currentInvoice.filePath]);

  return (
    <Box
      sx={{
        width: { lg: '50%', xs: '100%' },
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
      }}
    >
      {pdfFileUrl && isPdf && (
        <Worker workerUrl={PDF_JS_WORKER_URL || ''}>
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden'
            }}
          >
            <Box sx={{ flex: 1, overflow: 'hidden' }}>
              <PdfViewerWithWatermark
                fileUrl={pdfFileUrl}
                plugins={[zoomPluginInstance, rotatePluginInstance]}
                watermarkText={currentInvoice.stage as string}
              />
            </Box>
            <Box sx={zoomButtonsWrapper}>
              <RotateBackwardButton />
              <ZoomOutButton />
              <ZoomPopover />
              <ZoomInButton />
              <RotateForwardButton />
              <Button
                variant="text"
                color="primary"
                onClick={handleDownloadInvoice}
              >
                <FileDownloadIcon />
              </Button>
            </Box>
          </Box>
        </Worker>
      )}

      {pdfFileUrl && !isPdf && (
        <Box sx={{ flex: 1, overflow: 'auto', width: '100%' }}>
          <img
            src={pdfFileUrl}
            alt="Preview invoice"
            style={{
              width: '100%',
              height: 'auto'
            }}
          />
        </Box>
      )}

      {!pdfFileUrl && (
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <CircularProgress />
        </Box>
      )}
    </Box>
  );
};
